import { Controller } from "@hotwired/stimulus"

// Page-level drag-and-drop for the Files browser (TASK-158.4, narrowed to the
// drag itself in TASK-158.5).
//
// This controller knows about the DRAG and nothing else: it raises the overlay
// that names the destination, and hands whatever was dropped to the upload
// queue, which both this and the Add Files modal feed. Uploading, size limits
// and per-file state all live there, in one place, rather than once per entry
// point.
//
// Drag events fire on every element the pointer crosses, so a naive
// dragenter/dragleave pair flickers the overlay on and off as the cursor moves
// between children. The depth counter is the standard fix: count enters and
// leaves, and only hide at zero.
export default class extends Controller {
  static targets = ["overlay"]

  connect() {
    this.depth = 0

    this.boundEnter = this.dragEnter.bind(this)
    this.boundOver = this.dragOver.bind(this)
    this.boundLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    this.element.addEventListener("dragenter", this.boundEnter)
    this.element.addEventListener("dragover", this.boundOver)
    this.element.addEventListener("dragleave", this.boundLeave)
    this.element.addEventListener("drop", this.boundDrop)
  }

  disconnect() {
    this.element.removeEventListener("dragenter", this.boundEnter)
    this.element.removeEventListener("dragover", this.boundOver)
    this.element.removeEventListener("dragleave", this.boundLeave)
    this.element.removeEventListener("drop", this.boundDrop)
  }

  // Dragging selected text or a link across the page is not an upload, so the
  // overlay only answers to a drag that is actually carrying files.
  carriesFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  dragEnter(event) {
    if (!this.carriesFiles(event)) return

    event.preventDefault()
    this.depth += 1
    this.overlayTarget.classList.remove("hidden")
  }

  // Without preventDefault on dragover the browser refuses the drop entirely.
  dragOver(event) {
    if (!this.carriesFiles(event)) return

    event.preventDefault()
  }

  dragLeave(event) {
    if (!this.carriesFiles(event)) return

    event.preventDefault()
    this.depth -= 1
    if (this.depth <= 0) this.hide()
  }

  drop(event) {
    if (!this.carriesFiles(event)) return

    event.preventDefault()
    this.hide()

    const files = event.dataTransfer.files
    if (files.length === 0) return

    // The queue is on this same element, so the action wiring in the view is
    // all that connects them.
    this.dispatch("enqueue", { detail: { files } })
  }

  hide() {
    this.depth = 0
    this.overlayTarget.classList.add("hidden")
  }
}
